import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBook,
  faFileAlt,
  faChevronDown,
  faChevronUp,
  faBug,
  faFeather,
  faLightbulb,
  faHandsHelping,
  faHeart,
  faExternalLinkAlt,
} from "@fortawesome/free-solid-svg-icons";
import JSONViewer from "../tasks/JSONViewer";
import LogViewer from "../tasks/LogViewer";
import HttpRequestTracker from "./HttpRequestTracker";

const API_URL = process.env.REACT_APP_API_URL;
const fetchConfig = {
  credentials: "include",
  headers: { "Content-Type": "application/json", Accept: "application/json" },
};

const CourseView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [task, setTask] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [showJSON, setShowJSON] = useState(false);
  const [expandedChapters, setExpandedChapters] = useState({});
  const [activeLogUrl, setActiveLogUrl] = useState(null);
  const [loadingChapters, setLoadingChapters] = useState({});
  const [selectedChapters, setSelectedChapters] = useState([]);
  const [contentTypes, setContentTypes] = useState({
    notes: false,
    quiz: false,
    podcast: false,
  });
  const [detailLevel, setDetailLevel] = useState("medium");
  const [modelSize, setModelSize] = useState("small");
  const [specificInstructions, setSpecificInstructions] = useState("");
  const [docMinerStatus, setDocMinerStatus] = useState("idle");
  const [docMinerResponse, setDocMinerResponse] = useState(null);
  const [docMinerError, setDocMinerError] = useState(null);

  // Initialize with empty values
  const [docMinerForm, setDocMinerForm] = useState({
    pdfUrl: "",
  });

  // Initialize with empty values
  const [newCourseForm, setNewCourseForm] = useState({
    pdfUrl: "",
    title: "",
    modelSize: "small",
    detailLevel: "medium",
    isPremium: false,
    forcedLabelsDocminer: "", // Add new field for JSON input
  });

  // Add handlers
  const handleDocMinerSubmit = async (e) => {
    e.preventDefault();
    setDocMinerStatus("loading");
    setDocMinerResponse(null);
    setDocMinerError(null);
    try {
      const logger_task_id = task?._id ? `${task._id}_docminer` : "test";
      if (!docMinerForm.pdfUrl || !logger_task_id) {
        setDocMinerStatus("error");
        setDocMinerError("Missing PDF URL or logger_task_id");
        return;
      }
      const res = await fetch(`${API_URL}/support/docminer/extract`, {
        ...fetchConfig,
        method: "POST",
        body: JSON.stringify({ file_url: docMinerForm.pdfUrl, logger_task_id }),
      });
      const data = await res.json();
      if (!res.ok) {
        setDocMinerStatus("error");
        setDocMinerError(data);
      } else {
        setDocMinerStatus("success");
        setDocMinerResponse(data);
      }
    } catch (err) {
      setDocMinerStatus("error");
      setDocMinerError(err.message || err);
    }
  };

  const [courseGenStatus, setCourseGenStatus] = useState("idle");
  const [courseGenResponse, setCourseGenResponse] = useState(null);
  const [courseGenError, setCourseGenError] = useState(null);
  const [courseGenTaskId, setCourseGenTaskId] = useState(null);
  const [courseGenTaskStatus, setCourseGenTaskStatus] = useState(null);
  const [courseGenPolling, setCourseGenPolling] = useState(false);
  const [courseGenPollError, setCourseGenPollError] = useState(null);
  const COURSE_GEN_USER_ID = "66f2af3fd885089a58f9ecb3";

  const handleNewCourseSubmit = async (e) => {
    e.preventDefault();
    setCourseGenStatus("loading");
    setCourseGenResponse(null);
    setCourseGenError(null);
    setCourseGenTaskId(null);
    setCourseGenTaskStatus(null);
    setCourseGenPolling(false);
    setCourseGenPollError(null);
    try {
      // Compute preferred deployments for quiz and flashcards
      const preferred_deployments =
        newCourseForm.modelSize !== "mini"
          ? {
              quiz: [
                newCourseForm.modelSize === "small"
                  ? "azure-swedencentral-gpt4.1-mini"
                  : "azure-swedencentral-gpt4.1",
              ],
              flashcards: [
                newCourseForm.modelSize === "small"
                  ? "azure-swedencentral-gpt4.1-mini"
                  : "azure-swedencentral-gpt4.1",
              ],
            }
          : undefined;

      // Parse forced labels JSON if provided
      let forced_labels_docminer = undefined;
      if (newCourseForm.forcedLabelsDocminer.trim()) {
        try {
          forced_labels_docminer = JSON.parse(
            newCourseForm.forcedLabelsDocminer
          );
        } catch (jsonError) {
          setCourseGenStatus("error");
          setCourseGenError(
            `Invalid JSON in forced labels: ${jsonError.message}`
          );
          return;
        }
      }

      const body = {
        premium_mode: newCourseForm.isPremium,
        user_id: COURSE_GEN_USER_ID,
        file_url: newCourseForm.pdfUrl,
        title: newCourseForm.title,
        details_ratio: newCourseForm.detailLevel,
        lang: "fr",
        speaker: "nicolas",
        ...(preferred_deployments && { preferred_deployments }),
        ...(forced_labels_docminer && { forced_labels_docminer }),
      };
      const res = await fetch(`${API_URL}/support/orchestrator/build-course`, {
        ...fetchConfig,
        method: "POST",
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok || !data.task_id) {
        setCourseGenStatus("error");
        setCourseGenError(data);
      } else {
        setCourseGenStatus("success");
        setCourseGenResponse(data);
        setCourseGenTaskId(data.task_id);
        setCourseGenPolling(true);
      }
    } catch (err) {
      setCourseGenStatus("error");
      setCourseGenError(err.message || err);
    }
  };

  // Polling for course generation task status
  useEffect(() => {
    let pollInterval;
    const isAllChaptersDone = (task) => {
      if (
        !task.chapters ||
        !Array.isArray(task.chapters) ||
        task.chapters.length === 0
      )
        return false;
      return task.chapters.every(
        (ch) => ch.notes && ch.podcast && (ch.quiz || ch.quizz)
      );
    };
    const pollTaskStatus = async () => {
      if (!courseGenTaskId || !courseGenPolling) return;
      try {
        const res = await fetch(
          `${API_URL}/support/orchestrator/task_status/${courseGenTaskId}`,
          { ...fetchConfig, method: "GET" }
        );
        const data = await res.json();
        if (data && data.task) {
          setCourseGenTaskStatus(data.task);
          if (data.task.status === "done" && isAllChaptersDone(data.task)) {
            setCourseGenPolling(false);
          }
          if (data.task.failed) {
            setCourseGenPolling(false);
          }
        } else {
          setCourseGenPollError("No task data returned");
        }
      } catch (err) {
        setCourseGenPollError(err.message || err);
      }
    };
    if (courseGenPolling && courseGenTaskId) {
      pollInterval = setInterval(pollTaskStatus, 3000);
      pollTaskStatus();
    }
    return () => {
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [courseGenPolling, courseGenTaskId]);

  useEffect(() => {
    const fetchCourse = async () => {
      try {
        console.log("Fetching course with ID:", id);
        const response = await fetch(`${API_URL}/support/course/${id}`, {
          ...fetchConfig,
          method: "GET",
        });
        const data = await response.json();
        console.log("Full course response data:", data);
        console.log("Course chapters:", data.course?.chapters);
        setCourse(data.course);
        setTask(data.task);
        setReviews(data.reviews || []);
      } catch (error) {
        console.error("Error fetching course:", error);
      }
    };
    fetchCourse();
  }, [id]);

  useEffect(() => {
    console.log("COURSE:", course);
    if (task?.origin_url || course?.origin_url) {
      setDocMinerForm((prev) => ({
        ...prev,
        pdfUrl: task?.origin_url || course?.origin_url,
      }));
      setNewCourseForm((prev) => ({
        ...prev,
        pdfUrl: task?.origin_url || course?.origin_url,
        title: course?.title + " v2" || "",
      }));
    }
  }, [task, course]);

  const toggleChapter = async (idx) => {
    setExpandedChapters((prev) => {
      const newState = { ...prev, [idx]: !prev[idx] };
      if (newState[idx]) {
        // Fetch chapter content when expanding
        fetchChapterContent(idx);
      }
      return newState;
    });
  };

  const fetchChapterContent = async (chapterIndex) => {
    try {
      setLoadingChapters((prev) => ({ ...prev, [chapterIndex]: true }));
      console.log(`Fetching content for chapter ${chapterIndex}`);
      const response = await fetch(
        `${API_URL}/support/course/${id}/chapter/${chapterIndex}`,
        { ...fetchConfig, method: "GET" }
      );
      const chapterData = await response.json();
      console.log("Fetched chapter data:", chapterData);

      setCourse((prev) => ({
        ...prev,
        chapters: prev.chapters.map((ch, idx) =>
          idx === chapterIndex ? { ...ch, ...chapterData } : ch
        ),
      }));
    } catch (error) {
      console.error(`Error fetching chapter ${chapterIndex} content:`, error);
    } finally {
      setLoadingChapters((prev) => ({ ...prev, [chapterIndex]: false }));
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case "bug":
        return faBug;
      case "feature":
        return faFeather;
      case "improvement":
        return faLightbulb;
      case "assistance":
        return faHandsHelping;
      case "appreciation":
        return faHeart;
      default:
        return faFileAlt;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case "bug":
      case "feature":
      case "improvement":
        return "#3273dc";
      case "assistance":
        return "#ffdd57";
      case "appreciation":
        return "#00d1b2";
      default:
        return "#3273dc";
    }
  };

  const handleShowLogs = (url) => {
    setActiveLogUrl(url);
  };

  const [transformStatus, setTransformStatus] = useState("idle");
  const [transformResponse, setTransformResponse] = useState(null);
  const [transformError, setTransformError] = useState(null);

  const handleTransformSubmit = async (e) => {
    e.preventDefault();
    setTransformStatus("loading");
    setTransformResponse(null);
    setTransformError(null);
    try {
      const idx = selectedChapters[0];
      const resCh = await fetch(
        `${API_URL}/support/course/${id}/chapter/${idx}`,
        { ...fetchConfig, method: "GET" }
      );
      const chData = await resCh.json();
      const notes = chData.sub_chapters || [];
      const flashnotes = {
        sections: notes.map((section) => ({
          section_title: section.title || "",
          themes: section.themes || [],
        })),
      };
      // Determine selected task types and preferred deployments
      const selectedTypes = Object.entries(contentTypes)
        .filter(([, active]) => active)
        .map(([type]) => type);
      const preferred_deployments =
        modelSize !== "mini"
          ? selectedTypes.reduce(
              (acc, type) => ({
                ...acc,
                [type]: [
                  modelSize === "small"
                    ? "azure-swedencentral-gpt4.1-mini"
                    : "azure-swedencentral-gpt4.1",
                ],
              }),
              {}
            )
          : undefined;
      const payload = {
        chapter_title: chData.title || `Chapter ${idx + 1}`,
        flashnotes,
        details_ratio: detailLevel,
        metadata: "",
        course_id: course._id,
        chapter_id: idx,
        task_id: task._id,
        is_premium: course.premium,
        task_type:
          selectedTypes.length === 1 ? selectedTypes[0] : selectedTypes,
        ...(preferred_deployments && { preferred_deployments }),
      };
      const res = await fetch(`${API_URL}/support/generate_content`, {
        ...fetchConfig,
        method: "POST",
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) {
        setTransformStatus("error");
        setTransformError(data);
      } else {
        setTransformStatus("success");
        setTransformResponse(data);
      }
    } catch (err) {
      setTransformStatus("error");
      setTransformError(err.message || err);
    }
  };

  const openForceDocminer = () => {
    const pdfUrl = course?.origin_url || task?.origin_url || "";
    if (pdfUrl) {
      // Store the PDF URL in sessionStorage to avoid URL parameter issues
      sessionStorage.setItem("docminer_pdf_url", pdfUrl);
      navigate(`/force-docminer/${id}`);
    } else {
      alert("No PDF URL available for this course");
    }
  };

  if (!course) {
    return (
      <div className="container mt-6 has-text-centered">
        <div className="neumorphic-inset p-6 is-inline-block">
          <div
            className="loader is-loading"
            style={{ width: 50, height: 50 }}
          ></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4 mb-6">
      <div className="columns">
        {/* Main Course Info */}
        <div className="column is-8">
          <div className="neumorphic p-5">
            <div className="is-flex is-justify-content-space-between align-items-center mb-4">
              <div>
                <h1 className="title is-2 mb-2">
                  <FontAwesomeIcon icon={faBook} className="mr-3" />
                  {course.title}
                </h1>
                <div className="tags">
                  <span className="tag is-medium neumorphic">
                    {course.premium ? "Premium" : "Free"}
                  </span>
                </div>
              </div>
              <div className="buttons">
                <a
                  href={course.origin_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="button is-neumorphic"
                >
                  <span className="icon">
                    <FontAwesomeIcon icon={faExternalLinkAlt} />
                  </span>
                  <span>View PDF</span>
                </a>
                <button
                  className="button is-neumorphic"
                  onClick={openForceDocminer}
                >
                  <span className="icon">
                    <FontAwesomeIcon icon={faFileAlt} />
                  </span>
                  <span>Open in ForceDocminer</span>
                </button>
              </div>
            </div>

            {/* Task Info */}
            {task && (
              <div className="neumorphic-inset p-4 mb-4">
                <h2 className="title is-4 mb-3">Processing Information</h2>
                <div className="content">
                  <ul>
                    <li>Pages: {task.original_pages_length}</li>
                    <li>
                      Status:{" "}
                      <span className="tag is-medium">{task.status}</span>
                    </li>
                    <li>
                      Created at: {new Date(task.created_at).toLocaleString()}
                    </li>
                    <li>Errors: {task.errors?.length || 0}</li>
                  </ul>
                  {task.errors?.length > 0 && (
                    <div className="notification is-danger is-light">
                      {task.errors.map((error, idx) => (
                        <p key={idx}>{error}</p>
                      ))}
                    </div>
                  )}
                  <div className="buttons">
                    <button
                      className="button is-neumorphic mt-3"
                      onClick={() =>
                        handleShowLogs(
                          `https://studizycloud.blob.core.windows.net/logs/${task._id}_orchestrator.log?sv=2022-11-02&ss=bfqt&srt=o&sp=rwdlacupitfx&se=2025-12-25T21:40:26Z&st=2024-12-25T13:40:26Z&spr=https&sig=5Zl1tHxr%2BfNDWtmdRKWngqw0Bd5R1ZJFGQDp6IdlxkQ%3D`
                        )
                      }
                    >
                      <span className="icon">
                        <FontAwesomeIcon icon={faFileAlt} />
                      </span>
                      <span>Task Logs</span>
                    </button>
                    <button
                      className="button is-neumorphic mt-3"
                      onClick={() =>
                        handleShowLogs(
                          `https://studizycloud.blob.core.windows.net/logs/${task._id}_docminer_llm.log?sv=2022-11-02&ss=bfqt&srt=o&sp=rwdlacupitfx&se=2025-12-25T21:40:26Z&st=2024-12-25T13:40:26Z&spr=https&sig=5Zl1tHxr%2BfNDWtmdRKWngqw0Bd5R1ZJFGQDp6IdlxkQ%3D`
                        )
                      }
                    >
                      <span className="icon">
                        <FontAwesomeIcon icon={faFileAlt} />
                      </span>
                      <span>DocMiner Logs</span>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Chapters */}
            <h2 className="title is-4 mb-3">Chapters</h2>
            {!course.chapters || course.chapters.length === 0 ? (
              <div className="notification is-warning">
                No chapters available for this course.
              </div>
            ) : (
              <div className="chapters-grid">
                {course.chapters.map((chapter, idx) => (
                  <div key={idx} className="neumorphic p-4 mb-4">
                    <div
                      className="is-flex is-justify-content-space-between align-items-center cursor-pointer"
                      onClick={() => toggleChapter(idx)}
                    >
                      <h3 className="title is-5 mb-0">
                        {chapter.title || `Chapter ${idx + 1}`}
                      </h3>
                      <FontAwesomeIcon
                        icon={
                          expandedChapters[idx] ? faChevronUp : faChevronDown
                        }
                      />
                    </div>

                    {expandedChapters[idx] && (
                      <div className="mt-4">
                        {loadingChapters[idx] ? (
                          <div className="has-text-centered p-4">
                            <div
                              className="loader is-loading"
                              style={{ width: 30, height: 30 }}
                            ></div>
                          </div>
                        ) : (
                          <>
                            <p className="mb-2">
                              Pages: {chapter.page_range || "N/A"}
                            </p>
                            <div className="content mb-3">
                              <ul>
                                <li>
                                  Notes:{" "}
                                  {chapter.sub_chapters &&
                                  chapter.sub_chapters.length > 0 ? (
                                    <span className="tag is-success">
                                      Available
                                    </span>
                                  ) : (
                                    <span className="tag is-warning">
                                      Not available
                                    </span>
                                  )}
                                </li>
                                <li>
                                  Podcast:{" "}
                                  {chapter.podcast?.progress ? (
                                    <span className="tag is-info">
                                      {chapter.podcast.progress}% complete
                                    </span>
                                  ) : (
                                    <span className="tag is-warning">
                                      Not started
                                    </span>
                                  )}
                                </li>
                                <li>
                                  Quiz:{" "}
                                  {chapter.questions &&
                                  chapter.questions.length > 0 ? (
                                    <span className="tag is-success">
                                      {chapter.questions.length} questions
                                    </span>
                                  ) : (
                                    <span className="tag is-warning">
                                      Not available
                                    </span>
                                  )}
                                </li>
                              </ul>
                            </div>

                            {/* Chapter Content Preview */}
                            {chapter.sub_chapters &&
                              chapter.sub_chapters.length > 0 && (
                                <div className="notification is-info is-light">
                                  <p className="has-text-weight-bold">
                                    Content Preview:
                                  </p>
                                  <p>
                                    {chapter.sub_chapters[0].content?.slice(
                                      0,
                                      150
                                    )}
                                    ...
                                  </p>
                                </div>
                              )}

                            <div className="buttons">
                              {chapter.questions &&
                                chapter.questions.length > 0 && (
                                  <button
                                    onClick={() =>
                                      setShowJSON(`${idx}.questions`)
                                    }
                                    className="button is-small is-neumorphic"
                                  >
                                    <span className="icon">
                                      <FontAwesomeIcon icon={faFileAlt} />
                                    </span>
                                    <span>View Quiz</span>
                                  </button>
                                )}
                              {chapter.sub_chapters &&
                                chapter.sub_chapters.length > 0 && (
                                  <button
                                    onClick={() =>
                                      setShowJSON(`${idx}.sub_chapters`)
                                    }
                                    className="button is-small is-neumorphic"
                                  >
                                    <span className="icon">
                                      <FontAwesomeIcon icon={faBook} />
                                    </span>
                                    <span>View Notes</span>
                                  </button>
                                )}
                              <button
                                className="button is-small is-neumorphic"
                                onClick={() =>
                                  handleShowLogs(
                                    `https://studizycloud.blob.core.windows.net/logs/${task._id}_llm_chapter_${idx}_llm.log?sv=2022-11-02&ss=bfqt&srt=o&sp=rwdlacupitfx&se=2025-12-25T21:40:26Z&st=2024-12-25T13:40:26Z&spr=https&sig=5Zl1tHxr%2BfNDWtmdRKWngqw0Bd5R1ZJFGQDp6IdlxkQ%3D`
                                  )
                                }
                              >
                                <span className="icon">
                                  <FontAwesomeIcon icon={faFileAlt} />
                                </span>
                                <span>Generation Logs</span>
                              </button>
                            </div>

                            {chapter.podcast?.url && (
                              <div className="mt-3">
                                <audio controls style={{ width: "100%" }}>
                                  <source
                                    src={chapter.podcast.url}
                                    type="audio/mpeg"
                                  />
                                  Your browser does not support the audio
                                  element.
                                </audio>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Reviews Sidebar */}
        <div className="column is-4">
          <div className="neumorphic p-5">
            <h2 className="title is-4 mb-4">Course Reviews</h2>
            {reviews.length === 0 ? (
              <p className="has-text-grey">No reviews yet</p>
            ) : (
              <div className="reviews-list">
                {reviews.map((review, idx) => (
                  <div key={idx} className="neumorphic-inset p-4 mb-4">
                    <div className="is-flex is-justify-content-space-between mb-2">
                      <div className="is-flex align-items-center">
                        <Link to={`/user/${review.user._id}`}>
                          <div
                            className="neumorphic mr-3 is-flex is-justify-content-center is-align-items-center"
                            style={{
                              width: 40,
                              height: 40,
                              borderRadius: "50%",
                              color: "var(--accent-primary)",
                            }}
                          >
                            {review.user.name[0].toUpperCase()}
                          </div>
                        </Link>
                        <div>
                          <Link to={`/user/${review.user._id}`}>
                            <p className="has-text-weight-bold">
                              {review.user.name}
                            </p>
                            <p className="is-size-7 has-text-grey">
                              {review.user.email}
                            </p>
                          </Link>
                        </div>
                      </div>
                      <div className="tags">
                        <span
                          className="tag is-medium"
                          style={{
                            color:
                              review.rating >= 4
                                ? "#00d1b2"
                                : review.rating >= 3
                                ? "#ffdd57"
                                : "#ff3860",
                          }}
                        >
                          {review.rating}/5
                        </span>
                      </div>
                    </div>

                    <Link
                      to={`/chats/${review._id}`}
                      style={{ display: "block" }}
                    >
                      <p className="mb-2">{review.content}</p>

                      {review.category && (
                        <span
                          className="tag is-small"
                          style={{ color: getCategoryColor(review.category) }}
                        >
                          <span className="icon">
                            <FontAwesomeIcon
                              icon={getCategoryIcon(review.category)}
                            />
                          </span>
                          <span>{review.category}</span>
                        </span>
                      )}
                    </Link>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Transform Section - Moved outside the main course box */}
      <form
        onSubmit={handleTransformSubmit}
        className="neumorphic p-5 mt-6 transform-section"
      >
        <h2 className="title is-4 mb-3">Transform Course Content</h2>

        <div className="field">
          <label className="label">Select Chapters to Transform</label>
          <div className="buttons mb-2">
            <button
              className="button is-small is-neumorphic"
              onClick={() =>
                setSelectedChapters(
                  Array.from(Array(course.chapters.length).keys())
                )
              }
            >
              Select All
            </button>
            <button
              className="button is-small is-neumorphic"
              onClick={() => setSelectedChapters([])}
            >
              Clear Selection
            </button>
          </div>
          <div className="select is-multiple is-fullwidth">
            <select
              multiple
              size={Math.min(course.chapters.length, 6)}
              value={selectedChapters}
              onChange={(e) => {
                const selected = Array.from(
                  e.target.selectedOptions,
                  (option) => parseInt(option.value)
                );
                setSelectedChapters(selected);
              }}
              className="neumorphic-inset"
            >
              {course.chapters.map((chapter, idx) => (
                <option key={idx} value={idx}>
                  {chapter.title || `Chapter ${idx + 1}`}{" "}
                  {chapter.page_range ? `(${chapter.page_range})` : ""}
                </option>
              ))}
            </select>
          </div>
          {selectedChapters.length > 0 && (
            <p className="help mt-2">
              {selectedChapters.length} chapter
              {selectedChapters.length > 1 ? "s" : ""} selected
            </p>
          )}
        </div>

        <div className="columns">
          <div className="column">
            <div className="field">
              <label className="label">Content Types</label>
              <div
                className="neumorphic-inset p-4"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "1rem",
                }}
              >
                {[
                  ["notes", "Notes"],
                  ["quiz", "Quiz"],
                  ["podcast", "Podcast"],
                ].map(([key, label]) => (
                  <label
                    key={key}
                    className="checkbox"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "0.5rem",
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="checkbox"
                      style={{ marginRight: "1rem", cursor: "pointer" }}
                      checked={contentTypes[key]}
                      onChange={(e) =>
                        setContentTypes((prev) => ({
                          ...prev,
                          [key]: e.target.checked,
                        }))
                      }
                    />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="column">
            <div className="field">
              <label className="label">Detail Level</label>
              <div
                className="neumorphic-inset p-4"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "1rem",
                }}
              >
                {["low", "medium", "high", "super detailed"].map((level) => (
                  <label
                    key={level}
                    className="radio"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "0.5rem",
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="radio"
                      style={{ marginRight: "1rem", cursor: "pointer" }}
                      checked={detailLevel === level}
                      onChange={() => setDetailLevel(level)}
                      name="detailLevel"
                    />
                    <span>
                      {level.charAt(0).toUpperCase() + level.slice(1)}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="column">
            <div className="field">
              <label className="label">Model Size</label>
              <div
                className="neumorphic-inset p-4"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "1rem",
                }}
              >
                {["mini", "small", "large"].map((size) => (
                  <label
                    key={size}
                    className="radio"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "0.5rem",
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="radio"
                      style={{ marginRight: "1rem", cursor: "pointer" }}
                      checked={modelSize === size}
                      onChange={() => setModelSize(size)}
                      name="modelSize"
                    />
                    <span>{size.charAt(0).toUpperCase() + size.slice(1)}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="field mt-4">
          <label className="label">Specific Instructions</label>
          <div className="control">
            <textarea
              className="textarea neumorphic-inset"
              placeholder="Enter any specific instructions or requirements for the transformation..."
              value={specificInstructions}
              onChange={(e) => setSpecificInstructions(e.target.value)}
              rows="4"
            />
          </div>
        </div>

        <div className="field mt-4">
          <div className="control">
            <button
              type="submit"
              className="button is-neumorphic is-medium is-fullwidth"
              disabled={selectedChapters.length === 0}
            >
              Start Transformation
            </button>
          </div>
        </div>
        <HttpRequestTracker
          status={transformStatus}
          response={transformResponse}
          error={transformError}
          metrics={["status"]}
        />
      </form>

      {/* Additional Forms Section */}
      <div className="neumorphic p-5 mt-6 forms-section">
        <div className="columns">
          {/* Test DocMiner Form */}
          <div className="column">
            <h2 className="title is-4 mb-3">Test DocMiner</h2>
            <form
              onSubmit={handleDocMinerSubmit}
              className="neumorphic-inset p-4"
            >
              <div className="field">
                <label className="label">PDF URL</label>
                <div className="control">
                  <input
                    type="text"
                    className="input neumorphic-inset"
                    placeholder="Enter PDF URL"
                    value={docMinerForm.pdfUrl}
                    onChange={(e) =>
                      setDocMinerForm((prev) => ({
                        ...prev,
                        pdfUrl: e.target.value,
                      }))
                    }
                  />
                </div>
              </div>
              {/* Removed page range field */}
              <div className="field mt-4">
                <div className="control">
                  <button
                    type="submit"
                    className="button is-neumorphic is-fullwidth"
                    disabled={!docMinerForm.pdfUrl}
                  >
                    Test DocMiner
                  </button>
                </div>
              </div>
            </form>
            <HttpRequestTracker
              status={docMinerStatus}
              response={docMinerResponse}
              error={docMinerError}
              metrics={["duration", "pages", "status"]}
            />
          </div>

          {/* Generate New Course Form */}
          <div className="column">
            <h2 className="title is-4 mb-3">Generate New Course</h2>
            <form
              onSubmit={handleNewCourseSubmit}
              className="neumorphic-inset p-4"
            >
              <div className="field">
                <label className="label">PDF URL</label>
                <div className="control">
                  <input
                    type="text"
                    className="input neumorphic-inset"
                    placeholder="Enter PDF URL"
                    value={newCourseForm.pdfUrl}
                    onChange={(e) =>
                      setNewCourseForm((prev) => ({
                        ...prev,
                        pdfUrl: e.target.value,
                      }))
                    }
                  />
                </div>
              </div>

              <div className="field">
                <label className="label">Course Title</label>
                <div className="control">
                  <input
                    type="text"
                    className="input neumorphic-inset"
                    placeholder="Enter course title"
                    value={newCourseForm.title}
                    onChange={(e) =>
                      setNewCourseForm((prev) => ({
                        ...prev,
                        title: e.target.value,
                      }))
                    }
                  />
                </div>
              </div>

              <div className="field">
                <label className="label">Detail Level</label>
                <div
                  className="neumorphic-inset p-4"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "1rem",
                  }}
                >
                  {["low", "medium", "high", "super detailed"].map((level) => (
                    <label
                      key={level}
                      className="radio"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "0.5rem",
                        cursor: "pointer",
                      }}
                    >
                      <input
                        type="radio"
                        style={{ marginRight: "1rem", cursor: "pointer" }}
                        checked={newCourseForm.detailLevel === level}
                        onChange={() =>
                          setNewCourseForm((prev) => ({
                            ...prev,
                            detailLevel: level,
                          }))
                        }
                        name="newCourseDetailLevel"
                      />
                      <span>
                        {level.charAt(0).toUpperCase() + level.slice(1)}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="field">
                <label className="label">Model Size</label>
                <div
                  className="neumorphic-inset p-4"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "1rem",
                  }}
                >
                  {["mini", "small", "large"].map((size) => (
                    <label
                      key={size}
                      className="radio"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "0.5rem",
                        cursor: "pointer",
                      }}
                    >
                      <input
                        type="radio"
                        style={{ marginRight: "1rem", cursor: "pointer" }}
                        checked={newCourseForm.modelSize === size}
                        onChange={() =>
                          setNewCourseForm((prev) => ({
                            ...prev,
                            modelSize: size,
                          }))
                        }
                        name="newCourseModelSize"
                      />
                      <span>
                        {size.charAt(0).toUpperCase() + size.slice(1)}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="field">
                <label
                  className="checkbox"
                  style={{ display: "block", marginBottom: "1rem" }}
                >
                  <input
                    type="checkbox"
                    style={{ marginRight: "0.5rem" }}
                    checked={newCourseForm.isPremium}
                    onChange={(e) =>
                      setNewCourseForm((prev) => ({
                        ...prev,
                        isPremium: e.target.checked,
                      }))
                    }
                  />
                  Premium Course
                </label>
              </div>

              <div className="field">
                <label className="label">Forced Labels (JSON)</label>
                <div className="control">
                  <textarea
                    className="textarea neumorphic-inset"
                    placeholder="Enter forced labels in JSON format"
                    value={newCourseForm.forcedLabelsDocminer}
                    onChange={(e) =>
                      setNewCourseForm((prev) => ({
                        ...prev,
                        forcedLabelsDocminer: e.target.value,
                      }))
                    }
                    rows="4"
                  />
                </div>
              </div>

              <div className="field mt-4">
                <div className="control">
                  <button
                    type="submit"
                    className="button is-neumorphic is-fullwidth"
                    disabled={!newCourseForm.pdfUrl || !newCourseForm.title}
                  >
                    Generate Course
                  </button>
                </div>
              </div>
            </form>
            <HttpRequestTracker
              status={courseGenStatus}
              response={courseGenResponse}
              error={courseGenError}
              metrics={["task_id"]}
            />
            {courseGenTaskId && (
              <div
                className="neumorphic-inset p-4 mt-4"
                style={{ maxHeight: 350, overflow: "auto" }}
              >
                <h4 className="title is-6 mb-2">Live Task Status</h4>
                {courseGenTaskStatus ? (
                  <>
                    <div className="mb-2">
                      <span className="tag is-info is-medium">
                        {courseGenTaskStatus.status}
                      </span>
                      {courseGenTaskStatus.failed && (
                        <span className="tag is-danger is-medium ml-2">
                          Failed
                        </span>
                      )}
                    </div>
                    <div className="mb-2">
                      <strong>Step:</strong> {courseGenTaskStatus.step || "N/A"}
                    </div>
                    <div className="mb-2">
                      <strong>Chapters:</strong>{" "}
                      {courseGenTaskStatus.chapters_length || 0}
                    </div>
                    <div className="mb-2">
                      <strong>Progress:</strong>{" "}
                      {courseGenTaskStatus.progress || "N/A"}
                    </div>
                    {courseGenTaskStatus.errors &&
                      courseGenTaskStatus.errors.length > 0 && (
                        <div className="notification is-danger is-light">
                          {courseGenTaskStatus.errors.map((err, idx) => (
                            <div key={idx}>{err}</div>
                          ))}
                        </div>
                      )}
                    {courseGenTaskStatus.chapters &&
                      courseGenTaskStatus.chapters.length > 0 && (
                        <div className="mt-3">
                          <table className="table is-fullwidth is-bordered">
                            <thead>
                              <tr>
                                <th>Chapter</th>
                                <th>Notes</th>
                                <th>Podcast</th>
                                <th>Quiz</th>
                              </tr>
                            </thead>
                            <tbody>
                              {courseGenTaskStatus.chapters.map((ch, idx) => (
                                <tr key={idx}>
                                  <td>Chapter {idx + 1}</td>
                                  <td>{ch.notes ? "✓" : "×"}</td>
                                  <td>{ch.podcast ? "✓" : "×"}</td>
                                  <td>{ch.quizz ? "✓" : "×"}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                  </>
                ) : courseGenPollError ? (
                  <div className="has-text-danger">{courseGenPollError}</div>
                ) : (
                  <div className="has-text-grey">
                    Waiting for task status...
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {showJSON && (
        <JSONViewer
          url={`https://studizycloud.blob.core.windows.net/courses/${course._id}/chapters.${showJSON}.json?sv=2022-11-02&ss=bfqt&srt=o&sp=rwdlacupitfx&se=2025-12-25T21:40:26Z&st=2024-12-25T13:40:26Z&spr=https&sig=5Zl1tHxr%2BfNDWtmdRKWngqw0Bd5R1ZJFGQDp6IdlxkQ%3D`}
          close={() => setShowJSON(false)}
          idx={showJSON.split(".")[0]}
          label={showJSON.split(".")[1]}
        />
      )}

      {activeLogUrl && (
        <LogViewer
          url={activeLogUrl}
          close={() => setActiveLogUrl(null)}
          idx={task?.chapter_index || 0}
          label="Task Logs"
        />
      )}
    </div>
  );
};

export default CourseView;
